import type { DragEvent } from "react";

import { Skeleton } from "@monorepo-apps/ui/src/components/skeleton";

import type { FeatureRow } from "@/infrastructure/supabase/database.types";
import { FeatureCard, FeatureCardSkeleton } from "@/presentation/components/roadmaps/feature-card";
import {
  VERDICT_COLOR,
  VERDICT_COLOR_SOFT,
  VERDICT_DESCRIPTION,
  VERDICT_LABEL,
  type Verdict,
} from "@/presentation/data/roadmaps";

interface VerdictColumnProps {
  verdict: Verdict;
  features: readonly FeatureRow[];
  isOver: boolean;
  isLoading?: boolean;
  onDragOver: (e: DragEvent<HTMLDivElement>, verdict: Verdict) => void;
  onDragLeave: () => void;
  onDrop: (e: DragEvent<HTMLDivElement>, verdict: Verdict) => void;
  onCardDragStart: (e: DragEvent<HTMLDivElement>, feature: FeatureRow) => void;
  onInfo: (feature: FeatureRow) => void;
  onEdit: (feature: FeatureRow) => void;
  onRemove: (feature: FeatureRow) => void;
}

export function VerdictColumn({
  verdict,
  features,
  isOver,
  isLoading = false,
  onDragOver,
  onDragLeave,
  onDrop,
  onCardDragStart,
  onInfo,
  onEdit,
  onRemove,
}: VerdictColumnProps) {
  const accent = VERDICT_COLOR[verdict];
  const fill = VERDICT_COLOR_SOFT[verdict];
  return (
    <div
      data-slot="verdict-column"
      data-verdict={verdict}
      data-over={isOver}
      onDragOver={(e) => onDragOver(e, verdict)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, verdict)}
      className="flex min-w-[260px] flex-1 flex-col rounded-lg border border-[oklch(0.91_0.008_240)] bg-white/60 transition-colors"
      style={isOver ? { background: fill, borderColor: accent } : undefined}
    >
      <ColumnHeader
        verdict={verdict}
        count={features.length}
        accent={accent}
        isLoading={isLoading}
      />
      <div className="flex flex-1 flex-col gap-1.5 overflow-y-auto p-2.5">
        {isLoading ? (
          <>
            <FeatureCardSkeleton />
            <FeatureCardSkeleton />
            <FeatureCardSkeleton />
          </>
        ) : features.length === 0 ? (
          <ColumnEmpty isOver={isOver} />
        ) : (
          features.map((f) => (
            <FeatureCard
              key={f.id}
              feature={f}
              variant="placed"
              verdict={verdict}
              onDragStart={onCardDragStart}
              onInfo={onInfo}
              onEdit={onEdit}
              onRemove={onRemove}
            />
          ))
        )}
      </div>
    </div>
  );
}

function ColumnHeader({
  verdict,
  count,
  accent,
  isLoading,
}: {
  verdict: Verdict;
  count: number;
  accent: string;
  isLoading: boolean;
}) {
  return (
    <header
      className="rounded-t-lg border-b border-[oklch(0.91_0.008_240)] px-3 py-2.5"
      style={{ borderTop: `3px solid ${accent}` }}
    >
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-foreground text-[13px] font-semibold tracking-tight">
          {VERDICT_LABEL[verdict]}
        </h3>
        {isLoading ? (
          <Skeleton className="h-3.5 w-5 rounded" />
        ) : (
          <span
            className="font-data inline-flex min-w-5 items-center justify-center rounded px-1.5 py-0.5 text-[10px] font-medium tabular-nums"
            style={{ background: `${accent}1a`, color: accent }}
          >
            {count}
          </span>
        )}
      </div>
      <p className="text-muted-foreground mt-0.5 text-[10.5px] leading-snug">
        {VERDICT_DESCRIPTION[verdict]}
      </p>
    </header>
  );
}

function ColumnEmpty({ isOver }: { isOver: boolean }) {
  return (
    <div
      className={`flex min-h-32 flex-1 items-center justify-center rounded-md border-2 border-dashed text-center text-[11px] transition-colors ${
        isOver
          ? "border-foreground/40 text-foreground/70"
          : "border-foreground/15 text-muted-foreground/60"
      }`}
    >
      <p className="px-4">{isOver ? "Drop here" : "Drag features to classify"}</p>
    </div>
  );
}
